import { ConfirmationDialog, PoolDropzone, PoolModal, PoolTypography } from '@synergy/components';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormControlLabel, FormHelperText, Stack, Switch, Theme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection } from '@synergy/components-palo';
import { ReactNode, useState } from 'react';
import { StarIcon } from '@synergy/theme';
import { fields } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { uploadableImageMediaTypes } from '@synergy/models';
import urljoin from 'url-join';

import * as SupplierDetailOverviewActions from '../store/supplier-detail-overview-actions';
import { PaloAccountLogo } from '../../../../components/palo-account-logo';
import { PaloSwitchLabel } from '../../../../../../common/components/palo/palo-switch-label';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';
import { supplierSelector } from '../store/supplier-detail-overview-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    validFileTypes: {
        marginBottom: theme.spacing(2),
    },
}));

/**
 * The section of the head: the logo, the supplier's switches and the grid of
 * fields handed in (#2261).
 */
export const SupplierDetailOverviewBasicInfos = ({ children }: { children: ReactNode }) => {
    const classes = useStyles();
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const supplier = useAppSelector(supplierSelector);
    const apiBaseUrl = useAppSelector(state => state.settings.apiBaseUrl);
    const imageBaseUrl = useAppSelector(state => state.settings.imageCachePaths?.['184x79']);
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const fieldNames = fields<SupplierDetailOverviewFormValues>();

    const firstName = supplier?.name || intl.formatMessage({ id: 'app.contactsAndOrganizations.supplier.new' });

    const handleProfileImageUpload =
        (form: FormikProps<SupplierDetailOverviewFormValues>) =>
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError('image', 'app.error.invalidFileType');
                } else {
                    form.setFieldError('image', 'app.error.maxOneFile');
                }

                return;
            }

            dispatch(SupplierDetailOverviewActions.uploadProfileImage(acceptedFiles[0]));
            setIsUploadModalVisible(false);
        };

    const handleDeleteConfirm = () => {
        dispatch(SupplierDetailOverviewActions.removeProfileImage());
        setIsDeleteModalVisible(false);
    };

    const profileBaseSrc = apiBaseUrl && imageBaseUrl && urljoin(apiBaseUrl, imageBaseUrl);

    // The logo and the switches are the section's own settings and ride in its head.
    const settings = (
        <Stack gap={2} flexDirection="row" alignItems="flex-start">
            <Field name={fieldNames.image}>
                {({ field }: FieldProps<string>) => (
                    <PaloAccountLogo
                        name={firstName}
                        src={field.value && profileBaseSrc ? urljoin(profileBaseSrc, field.value) : undefined}
                        disabled={!supplier?.id}
                        onAdd={() => setIsUploadModalVisible(true)}
                        onRemove={() => setIsDeleteModalVisible(true)}
                    />
                )}
            </Field>
            <Field name={fieldNames.requireFunctionalAssets}>
                {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                        control={<Switch {...field} color="primary" checked={field.value} name={field.name} />}
                        label={
                            <PaloSwitchLabel>
                                <FormattedMessage id="app.common.requireFunctionalAssets" />
                            </PaloSwitchLabel>
                        }
                        sx={{ marginRight: 0 }}
                    />
                )}
            </Field>
            <Field name={fieldNames.isMain}>
                {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                        control={<Switch {...field} color="primary" checked={field.value} name={field.name} />}
                        label={
                            <PaloSwitchLabel>
                                <StarIcon />
                                <FormattedMessage id="app.common.mainContact" />
                            </PaloSwitchLabel>
                        }
                        sx={{ marginRight: 0 }}
                    />
                )}
            </Field>
        </Stack>
    );

    return (
        <PaloFormSection
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.headTitle" />}
            action={settings}
        >
            {children}

            <PoolModal
                isVisible={isUploadModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.uploadModal" />}
                handleClose={() => setIsUploadModalVisible(false)}
            >
                <Field name={fieldNames.image}>
                    {({ form, meta }: FieldProps<string>) => (
                        <>
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.detail.validFileTypes"
                                className={classes.validFileTypes}
                            />
                            <PoolDropzone
                                onDrop={handleProfileImageUpload(form)}
                                accept={uploadableImageMediaTypes}
                                multiple={true}
                                maxFiles={1}
                                useFsAccessApi={false}
                            />
                            {meta.error && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </>
                    )}
                </Field>
            </PoolModal>
            <ConfirmationDialog
                isVisible={isDeleteModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteLogo" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setIsDeleteModalVisible(false)}
                onConfirm={handleDeleteConfirm}
            >
                <FormattedMessage id="app.contactsAndOrganizations.detail.deleteLogoMessage" />
            </ConfirmationDialog>
        </PaloFormSection>
    );
};
